export type ServiceHealthStatus = "unknown" | "healthy" | "degraded";

export type ServiceHealthFailureReason =
  | "network"
  | "server"
  | "timeout"
  | "offline";

export interface ServiceHealthState {
  status: ServiceHealthStatus;
  consecutiveFailures: number;
  reason: ServiceHealthFailureReason | null;
  lastCheckedAt: number | null;
  lastHealthyAt: number | null;
  lastFailureAt: number | null;
}

export const SERVICE_HEALTH_FAILURE_THRESHOLD = 2;

const initialState: ServiceHealthState = {
  status: "unknown",
  consecutiveFailures: 0,
  reason: null,
  lastCheckedAt: null,
  lastHealthyAt: null,
  lastFailureAt: null,
};

type ServiceHealthListener = () => void;

let state = initialState;
const listeners = new Set<ServiceHealthListener>();

const publish = (nextState: ServiceHealthState): ServiceHealthState => {
  state = nextState;
  for (const listener of listeners) {
    listener();
  }
  return state;
};

export const getServiceHealthSnapshot = (): ServiceHealthState => state;

export const subscribeToServiceHealth = (
  listener: ServiceHealthListener
): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

export const reportServiceHealthSuccess = (
  checkedAt = Date.now()
): ServiceHealthState => {
  if (
    state.status === "healthy" &&
    state.consecutiveFailures === 0 &&
    state.reason === null
  ) {
    return state;
  }

  return publish({
    status: "healthy",
    consecutiveFailures: 0,
    reason: null,
    lastCheckedAt: checkedAt,
    lastHealthyAt: checkedAt,
    lastFailureAt: state.lastFailureAt,
  });
};

export const reportServiceHealthFailure = (
  reason: ServiceHealthFailureReason,
  checkedAt = Date.now()
): ServiceHealthState => {
  const consecutiveFailures = state.consecutiveFailures + 1;
  const status =
    state.status === "degraded" ||
    consecutiveFailures >= SERVICE_HEALTH_FAILURE_THRESHOLD
      ? "degraded"
      : "unknown";

  return publish({
    status,
    consecutiveFailures,
    reason,
    lastCheckedAt: checkedAt,
    lastHealthyAt: state.lastHealthyAt,
    lastFailureAt: checkedAt,
  });
};

export const shouldShowDegradedServiceBanner = (
  snapshot: ServiceHealthState = state
): boolean => snapshot.status === "degraded";

export const resetServiceHealthForTest = (): ServiceHealthState =>
  publish(initialState);
